import React from "react";
import { Box, Card, CardContent, Typography, useTheme } from "@mui/material";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Bar,
  ComposedChart,
  Line,
} from "recharts";
import { TimeSeriesDataPoint } from "../../../types/analytics";

interface RevenueChartProps {
  title: string;
  revenueData: TimeSeriesDataPoint[];
  orderCountData?: TimeSeriesDataPoint[];
  height?: number;
  showOrderCount?: boolean;
}

const formatCurrency = (value: number) => {
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
};

const RevenueChart: React.FC<RevenueChartProps> = ({
  title,
  revenueData,
  orderCountData,
  height = 300,
  showOrderCount = false,
}) => {
  const theme = useTheme();

  // Merge revenue and order count data by label
  const chartData = revenueData.map((r) => {
    const orderPoint = orderCountData?.find((o) => o.label === r.label);
    return {
      label: r.label,
      revenue: r.value,
      orders: orderPoint?.value ?? r.count,
    };
  });

  if (chartData.length === 0) {
    return (
      <Card sx={{ height: "100%" }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            {title}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: height - 60,
              color: "text.secondary",
            }}
          >
            <Typography>No data available</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {title}
        </Typography>
        <ResponsiveContainer width="100%" height={height}>
          {showOrderCount ? (
            <ComposedChart data={chartData}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={theme.palette.divider}
              />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 12 }}
                stroke={theme.palette.text.secondary}
              />
              <YAxis
                yAxisId="revenue"
                tickFormatter={formatCurrency}
                tick={{ fontSize: 12 }}
                stroke={theme.palette.text.secondary}
              />
              <YAxis
                yAxisId="orders"
                orientation="right"
                tick={{ fontSize: 12 }}
                stroke={theme.palette.text.secondary}
              />
              <Tooltip
                formatter={(value: any, name: any) => [
                  name === "revenue" ? `$${Number(value).toFixed(2)}` : value,
                  name === "revenue" ? "Revenue" : "Orders",
                ]}
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 8,
                }}
              />
              <Bar
                yAxisId="revenue"
                dataKey="revenue"
                fill={theme.palette.primary.main}
                opacity={0.3}
                radius={[4, 4, 0, 0]}
              />
              <Line
                yAxisId="orders"
                dataKey="orders"
                stroke={theme.palette.secondary.main}
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </ComposedChart>
          ) : (
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor={theme.palette.primary.main}
                    stopOpacity={0.3}
                  />
                  <stop
                    offset="95%"
                    stopColor={theme.palette.primary.main}
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={theme.palette.divider}
              />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 12 }}
                stroke={theme.palette.text.secondary}
              />
              <YAxis
                tickFormatter={formatCurrency}
                tick={{ fontSize: 12 }}
                stroke={theme.palette.text.secondary}
              />
              <Tooltip
                formatter={(value: any) => [`$${Number(value).toFixed(2)}`, "Revenue"]}
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 8,
                }}
              />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke={theme.palette.primary.main}
                strokeWidth={2}
                fill="url(#revenueGrad)"
              />
            </AreaChart>
          )}
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default RevenueChart;
